
















































































import { EneriqTypesMixin } from '@/features/app-heating-system/components/mixins/measurements/eneriq-types-mixin';
import GroupsMixin from '@/features/core/components/mixins/groups';
import { defineComponent } from '@vue/composition-api';
import {
  HeatingSystemCollectionWithPaginationQuery,
  HeatingSystemCollectionWithPaginationQuery_heatingSystemsList_items,
  HeatingSystemCollectionWithPaginationQuery_heatingSystemsList_items_heatingSystemMeasurementGroups,
} from '@/hsc-api/queries/__generated__/HeatingSystemCollectionWithPaginationQuery';
import CustomerSelection from './CustomerSelection.vue';
import { AppCustomerViewQuery_customers_items } from '@/features/app-customer/views/customer/__generated__/AppCustomerViewQuery';
import {
  CoreTreeNodeBarControlPathQuery,
  CoreTreeNodeBarControlPathQuery_treeNodes_items_Directory_children_items,
  CoreTreeNodeBarControlPathQueryVariables,
} from '@/features/core/components/tree-node-bar-control/__generated__/CoreTreeNodeBarControlPathQuery';
import customerTreeNodesQuery from '@/features/core/components/tree-node-bar-control/path.gql';
import {
  fernwärmeTypes,
  HeatingSystemType,
  kesselTypes,
  solarthermieTypes,
  bhkwTypes,
  wärmepumpeTypes,
  filterHeatingSystemsByType,
  HEATING_SYSTEM_COMPONENT_TYPE_FILTERS,
} from '@/util/heating-systems';
import { ADMIN_APP_ENDPOINT } from '@/env';
import { APOLLO_CLIENT } from '@/features/core/container/model';
import { keycloak } from '@/keycloak';
import { ComponentGroup, getComponentsGroupNames, Decoder } from '@/util/modbus';
import {
  SpotQueryWithLatestMetrics,
  SpotQueryWithLatestMetrics_spots_items,
} from '../__generated__/SpotQueryWithLatestMetrics';
import AdvancedViewTableData from '@/features/app-heating-system/views/tree-node/heating-systems-list/components/AdvancedViewTableData.vue';
import heatingSystemsListQuery from '@/hsc-api/queries/HeatingSystemCollectionWithPaginationQuery.gql';
import metricsQuery from '@/features/app-heating-system/views/tree-node/spot-with-latest-metrics.gql';
import _debounce from 'lodash/debounce';
import modbusMappingsQuery from '@/features/app-heating-system/views/tree-node/get-modbus-mappings-cust-id.gql';
import { ModbusMappings_modbusMappingConfig, ModbusMappings } from '../__generated__/ModbusMappings';
import {
  GetPropertiesUnderCustomer,
  GetPropertiesUnderCustomer_treeNodes_items,
  GetPropertiesUnderCustomerVariables,
} from '../__generated__/GetPropertiesUnderCustomer';
import getCustomerPropertiesQuery from '../get-customer-properties.gql';

export type HeatingSystem = HeatingSystemCollectionWithPaginationQuery_heatingSystemsList_items & {
  health?: string;
};

type Customer = AppCustomerViewQuery_customers_items;
type MetricCsvMapping = { name: string; csvVersion: number };

export default defineComponent({
  components: {
    CustomerSelection,
    AdvancedViewTableData,
  },
  mixins: [EneriqTypesMixin, GroupsMixin],
  data() {
    return {
      isLoadingHeatingSystems: false,
      isLoadingMetrics: false,
      isLoadingSummaries: false,
      isLoadingTreeNodes: false,
      isLoadingDecoders: false,
      customerTreeNodes: [] as CoreTreeNodeBarControlPathQuery['treeNodes']['items'],
      filteredTreeNodes: [] as CoreTreeNodeBarControlPathQuery['treeNodes']['items'],
      expandedTreeNodes: [] as string[],
      directoryHeatingSystemMap: new Map<string, HeatingSystem[]>(),
      selectedCustomer: null as Customer | null,
      selectedHeatingSystemType: null as HeatingSystemType | null,
      skip: 0,
      take: 500,
      expandSignal: 0,
      collapseSignal: 0,
      heatingSystems: [] as HeatingSystem[],
      spots: [] as SpotQueryWithLatestMetrics['spots']['items'],
      modbusMetricNames: [] as string[],
      columnNames: [] as string[],
      searchQuery: null as string | null,
      showContextMenu: false,
      modbusMappingConfigs: [] as ModbusMappings_modbusMappingConfig[],
    };
  },
  computed: {
    isLoading(): boolean {
      return this.isLoadingTreeNodes || this.isLoadingHeatingSystems || this.isLoadingMetrics;
    },
    heatingSystemTypes(): HeatingSystemType[] {
      return this.$store.getters.advancedViewFilters;
    },
    heatingControlSpots(): SpotQueryWithLatestMetrics_spots_items[] {
      return this.spots.filter((spot: SpotQueryWithLatestMetrics_spots_items) => spot.role === 'HEATING_CONTROL');
    },
    getSpotIds(): string[] {
      const uniqueSpotIds = new Set<string>();
      this.heatingSystems.forEach((hs) => {
        hs.heatingSystemMeasurementGroups?.forEach((group) => {
          group.heatingSystemMeasurements?.forEach((measurement) => {
            uniqueSpotIds.add(measurement.spotId);
          });
        });
      });

      return Array.from(uniqueSpotIds);
    },
    filteredHeatingSystems(): HeatingSystem[] {
      const heatingSystems = Array.from(this.directoryHeatingSystemMap.values()).flat() as HeatingSystem[];
      return filterHeatingSystemsByType(heatingSystems, this.selectedHeatingSystemType);
    },
    getComponents(): ComponentGroup[] {
      let components = getComponentsGroupNames(this.filteredHeatingSystems);
      const componentFilters: Partial<Record<HeatingSystemType, (item: ComponentGroup) => boolean>> = {
        [HeatingSystemType.Kessel]: (item: ComponentGroup) => kesselTypes().includes(item.type),
        [HeatingSystemType.Fernwärme]: (item: ComponentGroup) => fernwärmeTypes().includes(item.type),
        [HeatingSystemType.BHKW]: (item: ComponentGroup) => bhkwTypes().includes(item.type),
        [HeatingSystemType.Wärmepumpe]: (item: ComponentGroup) => wärmepumpeTypes().includes(item.type),
        [HeatingSystemType.Solarthermieanlage]: (item: ComponentGroup) => solarthermieTypes().includes(item.type),
      };

      const selectedFilter = this.selectedHeatingSystemType
        ? componentFilters[this.selectedHeatingSystemType]
        : undefined;

      if (selectedFilter) {
        components = components.filter(selectedFilter);
      }
      return components;
    },
  },
  watch: {
    searchQuery(val: string | null) {
      this.updateSearchQuery(val, this);
    },
    heatingSystemTypes(types: HeatingSystemType[]) {
      if (types.length > 0) {
        // select first type by default
        this.selectedHeatingSystemType = types[0];
      }
    },
  },
  beforeDestroy() {
    // close heating system advanced view if user navigates to other pages.
    // this also restores/unhide the tree node sidebar.
    this.$store.commit('showAdvancedView', false);
    this.$store.commit('showEditMetricsModal', false);
  },
  methods: {
    expandAll() {
      this.expandSignal++;
    },
    collapseAll() {
      this.collapseSignal++;
    },
    handleContextMenuAction() {
      if (this.selectedCustomer?.id) {
        this.goToRegisters(this.selectedCustomer?.id);
      }
      this.hideContextMenu();
    },
    goToRegisters(customerId: string) {
      this.$router.push({ name: 'AppHeatingSystem/EditRegisters', params: { treeNodeId: customerId } });
    },
    hideContextMenu() {
      this.showContextMenu = false;
    },
    onSelectHeatingSystemType(val: HeatingSystemType | null) {
      this.selectedHeatingSystemType = val;
    },
    onChangeSearchBar(val: string | null) {
      this.searchQuery = val;
    },
    async fetchTreeNodesByCustomer(id: string) {
      try {
        this.isLoadingTreeNodes = true;

        const { data } = await this.$apollo.query<
          CoreTreeNodeBarControlPathQuery,
          CoreTreeNodeBarControlPathQueryVariables
        >({ query: customerTreeNodesQuery, variables: { id } });
        const { data: properties } = await this.$apollo.query<
          GetPropertiesUnderCustomer,
          GetPropertiesUnderCustomerVariables
        >({
          client: APOLLO_CLIENT.PORTAL_CLIENT,
          query: getCustomerPropertiesQuery,
          variables: {
            id,
            take: 10_000,
          },
          fetchPolicy: 'no-cache',
        });
        // Create a map of Directory IDs to their corresponding properties

        const propertyMap = data.treeNodes.items[0]?.children.items.reduce(
          (
            acc: Map<string, HeatingSystem[]>,
            node: CoreTreeNodeBarControlPathQuery_treeNodes_items_Directory_children_items,
          ) => {
            acc.set(
              node.id,
              Array.from(
                new Set(
                  properties.treeNodes.items
                    .filter(
                      (prop: GetPropertiesUnderCustomer_treeNodes_items) =>
                        prop.path.items.find((p) => p.id === node.id) &&
                        this.heatingSystems.some((hs) => hs.siteId === prop.id),
                    )
                    .map((p: GetPropertiesUnderCustomer_treeNodes_items) => ({
                      hs: this.heatingSystems.find((hs) => hs.siteId === p.id),
                    }))
                    .map((item) => {
                      if (!item.hs) return [] as HeatingSystem[];
                      // For heating systems with multiple HEATING_CONTROL devices, create dummy heating system instances
                      // This is to allow displaying multiple HEATING_CONTROL groups in the advanced view table for the same heating system
                      if (this.hasMultipleHeatingControlDevices(item.hs as HeatingSystem)) {
                        const heatingControlMeasurementGroups = item.hs?.heatingSystemMeasurementGroups.filter(
                          (group) =>
                            this.heatingControlSpots.some(
                              (heatingControlSpot: SpotQueryWithLatestMetrics_spots_items) =>
                                heatingControlSpot.id === group.spotId,
                            ),
                        );
                        // For dual temperature sensors, a (spotId) can only be assigned to one heating system component
                        // For HEATING_CONTROL devices, this isn't the case as the device can control multiple components
                        // Therefore, we need to create a map of spotId to measurement group to ensure uniqueness
                        // We don't want to show 10 rows just because a heating control device has 10 registers which were mapped to unique components
                        // We only want to show one row per heating control device
                        const measurementGroups = new Map<
                          string,
                          HeatingSystemCollectionWithPaginationQuery_heatingSystemsList_items_heatingSystemMeasurementGroups
                        >(
                          heatingControlMeasurementGroups?.map(
                            (
                              group: HeatingSystemCollectionWithPaginationQuery_heatingSystemsList_items_heatingSystemMeasurementGroups,
                            ) => [group.spotId, group],
                          ),
                        );
                        const firstMeasurementGroup = heatingControlMeasurementGroups?.[0].spotId;
                        const additionalHeatingControlGroups = Array.from(measurementGroups.entries())
                          .filter(
                            ([, group]) =>
                              group.spotId !== firstMeasurementGroup && measurementGroups.has(group.spotId),
                          )
                          .map(
                            ([, group]) =>
                              ({
                                ...group,
                                groupName: 'Heizungssteuerung',
                                systemComponentTypeName: 'HEATING_CONTROL',
                              } as HeatingSystemCollectionWithPaginationQuery_heatingSystemsList_items_heatingSystemMeasurementGroups),
                          );
                        // Modify the original heating system to only include the first HEATING_CONTROL group and other non-HEATING_CONTROL components
                        item.hs.heatingSystemMeasurementGroups = item.hs.heatingSystemMeasurementGroups.filter(
                          (group) => !measurementGroups.has(group.spotId) || group.spotId === firstMeasurementGroup,
                        );
                        return [
                          item.hs,
                          // Create a new dummy heating system instance for each additional HEATING_CONTROL group
                          ...additionalHeatingControlGroups.map((group) => ({
                            ...item.hs,
                            id: `${item.hs?.id}-${group.spotId}`, // create a unique ID for each heating system instance
                            heatingSystemMeasurementGroups: heatingControlMeasurementGroups.filter(
                              (g) => g.spotId === group.spotId,
                            ),
                          })),
                        ] as HeatingSystem[];
                      } else {
                        return [item.hs] as HeatingSystem[];
                      }
                    })
                    .flat(),
                ),
              ),
            );
            return acc;
          },
          new Map<string, HeatingSystem[]>(),
        );
        this.directoryHeatingSystemMap = propertyMap;
        this.customerTreeNodes = data.treeNodes.items;
        this.filteredTreeNodes = this.customerTreeNodes;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      } finally {
        this.isLoadingTreeNodes = false;
      }
    },
    hasMultipleHeatingControlDevices(hs: HeatingSystem): boolean {
      const heatingControlSpotIds = new Set(
        hs.heatingSystemMeasurementGroups
          .filter((group) =>
            this.heatingControlSpots.some(
              (heatingControlSpot: SpotQueryWithLatestMetrics_spots_items) => heatingControlSpot.id === group.spotId,
            ),
          )
          .map((m) => m.spotId),
      );
      return heatingControlSpotIds.size > 1;
    },
    async fetchHeatingSystems(id: string): Promise<void> {
      try {
        this.isLoadingHeatingSystems = true;
        const { data } = await this.$apollo.query<HeatingSystemCollectionWithPaginationQuery>({
          client: APOLLO_CLIENT.HEATING_SYSTEM_COLLECTOR_CLIENT,
          query: heatingSystemsListQuery,
          variables: {
            customerIdOrSiteId: id,
            skip: this.skip,
            take: this.take,
          },
          fetchPolicy: 'no-cache',
        });

        this.heatingSystems = data.heatingSystemsList.items.filter(
          (item) => item.heatingSystemMeasurementGroups.length > 0,
        );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      } finally {
        this.isLoadingHeatingSystems = false;
      }
    },
    async fetchMetrics(spotIds: string[]): Promise<void> {
      try {
        this.isLoadingMetrics = true;
        const { data } = await this.$apollo.query<SpotQueryWithLatestMetrics>({
          client: APOLLO_CLIENT.PORTAL_CLIENT,
          query: metricsQuery,
          variables: {
            spotIds,
            skip: 0,
            take: 10_000,
          },
          fetchPolicy: 'no-cache',
        });

        this.spots = data.spots.items;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      } finally {
        this.isLoadingMetrics = false;
      }
    },
    async modbusMappingsTable(customerId: string): Promise<void> {
      this.isLoadingDecoders = true;
      try {
        const { data } = await this.$apollo.query<ModbusMappings>({
          client: APOLLO_CLIENT.PORTAL_CLIENT,
          query: modbusMappingsQuery,
          variables: {
            customerId: customerId,
          },
        });
        this.modbusMappingConfigs = data.modbusMappingConfig;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      } finally {
        this.isLoadingDecoders = false;
      }
    },
    async fetchDecoders(manufacturer: string, model: string, role: string): Promise<Decoder[] | null> {
      try {
        this.isLoadingDecoders = true;
        let baseUrl = 'https://prod.backend.gk4null.de/';
        if (ADMIN_APP_ENDPOINT) {
          baseUrl = new URL('', ADMIN_APP_ENDPOINT).toString();
        }
        const queryString = new URLSearchParams({
          manufacturer,
          model,
          role,
        }).toString();
        const res = await fetch(`${baseUrl}modbus/api/decoders?${queryString}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            authorization: `Bearer ${keycloak?.token ?? ''}`,
          },
        });
        return res.ok ? await res.json() : null;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        return null;
      } finally {
        this.isLoadingDecoders = false;
      }
    },
    getModbusMetricNames() {
      let metricNames: MetricCsvMapping[] = [];
      metricNames.push(
        ...this.modbusMappingConfigs
          .filter((mapping) => mapping.csvVersion === 2)
          .flatMap((mapping) => mapping.mappingRules)
          .filter((rule) => rule?.tableData?.includes('graph'))
          .map((rule) => ({
            name: rule.metricName ?? rule.registerName,
            csvVersion: 2,
          })),
      );
      metricNames = metricNames
        .filter((config, index, self) => config.name && self.findIndex((c) => c.name === config.name) === index)
        .sort((a, b) => a.name.localeCompare(b.name));
      this.columnNames = metricNames.filter((c) => c.csvVersion === 1).map((c) => c.name);
      this.modbusMetricNames = metricNames.map((mmn) => mmn.name);
    },
    async onUpdateCustomer(customer: Customer): Promise<void> {
      this.selectedCustomer = customer;
      await this.modbusMappingsTable(customer.id);
      this.getModbusMetricNames();
      this.$store.commit('selectedCustomerName', this.selectedCustomer?.name);
      this.$store.commit('selectedCustomerId', this.selectedCustomer?.id);
      await this.fetchHeatingSystems(customer.id);
      this.setHeatingSystemFilters();
      await this.fetchMetrics(this.getSpotIds);

      this.fetchTreeNodesByCustomer(customer.id);
    },
    setHeatingSystemFilters(): void {
      const hasKessel = this.heatingSystems.some((hs) =>
        hs.heatingSystemMeasurementGroups?.some(
          (group) => group.systemComponentTypeName === HEATING_SYSTEM_COMPONENT_TYPE_FILTERS.BOILER,
        ),
      );

      const hasFernwaerme = this.heatingSystems.some((hs) =>
        hs.heatingSystemMeasurementGroups?.some(
          (group) =>
            group.systemComponentTypeName === HEATING_SYSTEM_COMPONENT_TYPE_FILTERS.TELE_HEATING ||
            group.systemComponentTypeName === HEATING_SYSTEM_COMPONENT_TYPE_FILTERS.TELE_HEATING_SECONDARY ||
            group.systemComponentTypeName === HEATING_SYSTEM_COMPONENT_TYPE_FILTERS.HEATING_CONTROL,
        ),
      );

      const hasBHKW = this.heatingSystems.some((hs) =>
        hs.heatingSystemMeasurementGroups?.some(
          (group) => group.systemComponentTypeName === HEATING_SYSTEM_COMPONENT_TYPE_FILTERS.COMBINED_HEAT_POWER_PLANT,
        ),
      );

      const hasWärmepumpe = this.heatingSystems.some((hs) =>
        hs.heatingSystemMeasurementGroups?.some(
          (group) =>
            group.systemComponentTypeName === HEATING_SYSTEM_COMPONENT_TYPE_FILTERS.HEAT_PUMP_PRIMARY ||
            group.systemComponentTypeName === HEATING_SYSTEM_COMPONENT_TYPE_FILTERS.HEAT_PUMP_SECONDARY,
        ),
      );

      const hasSolarthermieanlage = this.heatingSystems.some((hs) =>
        hs.heatingSystemMeasurementGroups?.some(
          (group) => group.systemComponentTypeName === HEATING_SYSTEM_COMPONENT_TYPE_FILTERS.SOLAR_HEATING,
        ),
      );

      const types: HeatingSystemType[] = [];
      if (hasKessel) types.push(HeatingSystemType.Kessel);
      if (hasFernwaerme) types.push(HeatingSystemType.Fernwärme);
      if (hasBHKW) types.push(HeatingSystemType.BHKW);
      if (hasWärmepumpe) types.push(HeatingSystemType.Wärmepumpe);
      if (hasSolarthermieanlage) types.push(HeatingSystemType.Solarthermieanlage);

      this.$store.commit('setAdvancedViewFilters', Array.from(types));
    },
    openMetricsEditModal(): void {
      this.$store.commit('selectedCustomerName', this.selectedCustomer?.name);
      this.$store.commit('selectedCustomerId', this.selectedCustomer?.id);
      this.$store.commit('showEditMetricsModal', true);
    },
    updateSearchQuery: _debounce(function (val: string | null, that: any) {
      that.$store.commit('setAdvancedViewSearchQuery', val);
    }, 500),
  },
});
